import { ConnectProto } from "@racoon/api";
import { useApproveRouteMutation } from "~/shared/api/routes/route.grpc";
import {
  useAddCrewMemberToShiftMutation,
  useSetJobConsignmentNotesMutation,
  useSetRouteConsignmentNotesMutation,
  useSetShiftConsignmentNoteMutation,
} from "~/shared/api/consignment-note/consignment-note.grpc";
import { buildTtnNotes, type TtnSnapshot } from "./ttnNotes";

/**
 * Writes a TTN and approves its trips. Note RPCs are partial and idempotent, so
 * the whole on-screen TTN is sent. The crew, trip and job writes are independent
 * and all start at once: one rejected call must not keep the others from being
 * sent. The shift note always goes last, whatever became of them: the backend
 * re-checks the TTN's completeness in each write's own transaction, so only a
 * write made after the others landed sees them all and can close the TTN. Trips
 * are approved only once every write landed; otherwise the first failure is thrown.
 * Resolves to the shift as the backend re-read it after the last write, with
 * `consignmentNoteDoneAt` set only if the TTN came out complete.
 */
export const useTtnWrites = () => {
  const [approveRoute] = useApproveRouteMutation();
  const [addCrewMemberToShift] = useAddCrewMemberToShiftMutation();
  const [setShiftNote] = useSetShiftConsignmentNoteMutation();
  const [setRouteNotes] = useSetRouteConsignmentNotesMutation();
  const [setJobNotes] = useSetJobConsignmentNotesMutation();

  return async (snapshot: TtnSnapshot) => {
    const { shift, routes, draft } = snapshot;
    const ownerOrganizationId = shift.ownerOrganizationId;
    const { shiftNote, routeNotes, jobNotesByProvider } = buildTtnNotes(snapshot);
    const writes: Promise<unknown>[] = [];

    // Despite its name the RPC replaces the crew: whoever is left out comes off
    // the shift, and an empty list (the last loader removed) clears it.
    if (draft.crewIds) {
      writes.push(
        addCrewMemberToShift({
          driverOnShiftId: shift.id,
          ownerOrganizationId,
          crewIds: draft.crewIds,
        }).unwrap(),
      );
    }
    if (routeNotes.length > 0) {
      writes.push(setRouteNotes({ ownerOrganizationId, notes: routeNotes }).unwrap());
    }
    for (const [serviceProviderId, notes] of jobNotesByProvider) {
      writes.push(setJobNotes({ serviceProviderId, notes }).unwrap());
    }
    const settled = await Promise.allSettled(writes);
    const [shiftWrite] = await Promise.allSettled([setShiftNote(shiftNote).unwrap()]);

    const failed = [...settled, shiftWrite].find(
      (result): result is PromiseRejectedResult => result.status === "rejected",
    );
    if (failed) throw failed.reason;

    await Promise.all(
      routes
        .filter((route) => route.status === ConnectProto.RouteStatus.NOT_APPROVED)
        .map((route) => approveRoute({ id: route.id, ownerOrganizationId }).unwrap()),
    );
    return shiftWrite.status === "fulfilled" ? shiftWrite.value : undefined;
  };
};
